import { ANSWER_TYPES } from '../../helpers/answer_types'
import { updateAnswerInArray } from '../../helpers/activities_helper'

// Each strategy returns a NEW answers object (shallow cloned) with updates applied.
// Caller is responsible for cloning the container before passing (so we don't mutate original state).

function applySelectionUpdate(existing, answerOne) {
  if (!answerOne?.selections?.length) return existing
  const next = { ...existing }
  answerOne.selections.forEach((newSelection) => {
    const current = next.selections ?? []
    next.selections = updateAnswerInArray(current, newSelection)
  })
  return next
}

function applyRankingUpdate(existing, answerOne) {
  if (!answerOne?.rankings) return existing
  return { ...existing, rankings: answerOne.rankings }
}

function applyFormFieldUpdate(existing, answers) {
  if (!answers?.length) return existing
  let next = { ...existing }
  answers.forEach((formFieldAnswer) => {
    // Skip descriptor / placeholder objects (e.g., first element containing only type/title)
    if (!formFieldAnswer?.activityId && !formFieldAnswer?.id) return
    const current = next.formFields ?? []
    next = { ...next, formFields: updateAnswerInArray(current, formFieldAnswer) }
  })
  return next
}

function applyConversationUpdate(existing, answerOne) {
  if (!answerOne) return existing
  const next = { ...existing }
  if (!next.conversations) next.conversations = []
  const idx = next.conversations.findIndex((c) => c?.activityId === answerOne.activityId)
  const normalized = { ...answerOne }
  if (!normalized.title) normalized.title = ''
  if (idx !== -1) next.conversations[idx] = { ...next.conversations[idx], ...normalized }
  else next.conversations.push(normalized)
  return next
}

/**
 * Applies updates to an existing answer object based on the provided answers array and answer type.
 *
 * For each answer type, a specific update strategy is applied:
 * - SELECTION: Updates the selections array using `applySelectionUpdate`.
 * - RANKING: Replaces the rankings array using `applyRankingUpdate`.
 * - FORM_FIELD: Updates form field answers using `applyFormFieldUpdate`.
 * - QUESTION_ANSWER / QUESTION_ANSWER_PHOTOS: Updates conversations using `applyConversationUpdate`.
 * Unsupported or unknown types result in no changes.
 *
 * The function returns a new shallow-cloned answer object with updates applied.
 * The caller is responsible for cloning the container before passing to avoid mutating original state.
 *
 * @param {Object} existing - The existing answer object to update. May be null or undefined.
 * @param {Array<Object>} answers - Array of answer objects to apply. The first element is used for most types.
 * @returns {Object} A new answer object with updates applied according to the answer type.
 */
export function applyAnswerUpdates(existing, answers) {
  if (!answers?.length) return existing
  const answerOne = answers[0]
  let next = existing ? { ...existing } : {}

  if (answerOne?.title) next.title = answerOne.title // Preserve title logic (Reducer also sets via prop outside)

  switch (answerOne?.type) {
    case ANSWER_TYPES.SELECTION:
      next = applySelectionUpdate(next, answerOne)
      break
    case ANSWER_TYPES.RANKING:
      next = applyRankingUpdate(next, answerOne)
      break
    case ANSWER_TYPES.FORM_FIELD:
      next = applyFormFieldUpdate(next, answers)
      break
    case ANSWER_TYPES.QUESTION_ANSWER:
    case ANSWER_TYPES.QUESTION_ANSWER_PHOTOS:
      next = applyConversationUpdate(next, answerOne)
      break
    default:
      // Unsupported / no-op types just return existing
      break
  }
  return next
}

// Note: internal strategy helpers intentionally not exported individually to keep public API minimal.
